import React from 'react';
import { Link } from 'react-router';
import type { LscArchiveItem, LscMatch } from '../types';
import {
  formatAwayAtHome,
  formatMatchIdentities,
  LEAGUE_SCHEDULES_PAGE_COPY,
} from '../leagueNav';
import { lscPath } from '../routing';
import TeamMatchupBadge from './leagueHome/TeamMatchupBadge';
import { displaySeasonBesideActiveEdition } from '../seasonDisplay';

export type MatchArchiveLinkageState = 'watch' | 'linked' | 'none';

export function resolveMatchArchiveLinkage(
  match: LscMatch,
  linkedArchives: LscArchiveItem[] = []
): MatchArchiveLinkageState {
  const hasPublishedArchive = linkedArchives.some(
    (archive) => archive.archive_status === 'published'
  );
  const linkedArchiveId =
    linkedArchives[0]?.id ?? match.linked_vod_archive_ids?.[0] ?? null;
  const isLinked =
    hasPublishedArchive ||
    Boolean(match.vod_linked) ||
    (match.linked_vod_archive_ids?.length ?? 0) > 0 ||
    linkedArchives.length > 0;

  if (linkedArchiveId && hasPublishedArchive) {
    return 'watch';
  }
  if (isLinked) {
    return 'linked';
  }
  return 'none';
}

interface MatchesTableProps {
  leagueId: string;
  matches: LscMatch[];
  archivesByMatchId?: Map<string, LscArchiveItem[]>;
  showMatchupBadges?: boolean;
  activeEdition?: string | null;
}

function formatWhen(value?: string): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const MatchesTable: React.FC<MatchesTableProps> = ({
  leagueId,
  matches,
  archivesByMatchId,
  showMatchupBadges = false,
  activeEdition,
}) => {
  if (!matches.length) {
    return (
      <p className="text-secondary-400 text-sm">{LEAGUE_SCHEDULES_PAGE_COPY.emptyState}</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-secondary-700">
      <table className="min-w-full text-sm">
        <thead className="bg-secondary-800 text-secondary-300">
          <tr>
            <th className="px-4 py-3 text-left font-medium">Season</th>
            <th className="px-4 py-3 text-left font-medium">Stage</th>
            <th className="px-4 py-3 text-left font-medium">{LEAGUE_SCHEDULES_PAGE_COPY.matchupColumn}</th>
            <th className="px-4 py-3 text-left font-medium">Identities</th>
            <th className="px-4 py-3 text-left font-medium">Scheduled</th>
            <th className="px-4 py-3 text-left font-medium">Lifecycle</th>
            <th className="px-4 py-3 text-left font-medium">Archive</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-700">
          {matches.map((match) => {
            const linkedArchives = archivesByMatchId?.get(match.id) ?? [];
            const primaryArchive = linkedArchives[0] ?? null;
            const linkedArchiveId =
              primaryArchive?.id ?? match.linked_vod_archive_ids?.[0] ?? null;
            const linkageState = resolveMatchArchiveLinkage(match, linkedArchives);
            const hasPublishedArchive = linkageState === 'watch';

            return (
              <tr key={match.id} className="hover:bg-secondary-800/50">
                <td className="px-4 py-3 text-secondary-300">
                  {displaySeasonBesideActiveEdition(
                    match.season_label,
                    match.edition,
                    activeEdition
                  ) || '—'}
                </td>
                <td className="px-4 py-3 text-secondary-300">{match.stage_label ?? '—'}</td>
                <td className="px-4 py-3 text-white" data-testid="matches-table-matchup">
                  {showMatchupBadges ? (
                    <div className="flex items-center gap-2 min-w-[12rem]">
                      <TeamMatchupBadge
                        label={match.away_side_label?.trim() || 'Away'}
                        side="away"
                        size="sm"
                      />
                      <span className="text-secondary-500 text-xs" aria-hidden>
                        @
                      </span>
                      <TeamMatchupBadge
                        label={match.home_side_label?.trim() || 'Home'}
                        side="home"
                        size="sm"
                      />
                    </div>
                  ) : (
                    formatAwayAtHome(match)
                  )}
                </td>
                <td className="px-4 py-3 text-secondary-400 text-xs" data-testid="matches-table-identities">
                  {formatMatchIdentities(match, primaryArchive)}
                </td>
                <td className="px-4 py-3 text-secondary-300">{formatWhen(match.scheduled_at)}</td>
                <td className="px-4 py-3 capitalize text-secondary-300">
                  {match.lifecycle_status ?? 'active'}
                </td>
                <td className="px-4 py-3 text-secondary-300" data-testid="matches-table-archive">
                  {linkedArchiveId ? (
                    <div className="flex items-center gap-2">
                      {hasPublishedArchive && (
                        <span className="inline-flex rounded-full bg-green-900/50 border border-green-700 px-2 py-0.5 text-xs text-green-200">
                          {LEAGUE_SCHEDULES_PAGE_COPY.archivePublishedLabel}
                        </span>
                      )}
                      <Link
                        to={lscPath(
                          `/leagues/${leagueId}/archive/${encodeURIComponent(linkedArchiveId)}`
                        )}
                        className="text-primary-300 hover:text-primary-200 text-xs"
                      >
                        {LEAGUE_SCHEDULES_PAGE_COPY.archiveWatchLabel}
                      </Link>
                    </div>
                  ) : linkageState === 'linked' ? (
                    LEAGUE_SCHEDULES_PAGE_COPY.archiveLinkedLabel
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MatchesTable;
